<template>
    <div class="bg-white">
        <!-- HERO -->
        <v-container fluid class="hero-section py-16 text-center">
            <h1 class="hero-title">
                Partner With Us
            </h1>
        </v-container>

        <v-container max-width="1200" class="py-10">
            <v-row align="center" style="--v-gutter-x: 28px; --v-gutter-y: 28px;">
                <!-- Left image -->
                <v-col cols="12" md="6">
                    <v-img :src=bannerImage alt="Service & Support" class="rounded-lg elevation-3" cover height="680" />
                </v-col>

                <!-- Right feature points -->
                <v-col cols="12" md="6">
                    <div class="text-medium-emphasis mx-auto mb-10"  style="font-size: 1.25rem; line-height: 1.8;">
                        Join a marketplace built by travel advisors, for travel advisors. Our platform helps you fill
                        unsold group
                        space, find new cruise and resort block deals, and connect with both travelers and fellow agents
                        — all in
                        one place.
                    </div>
                    <div class="text-h5 font-weight-bold mb-4">Why Us?</div>

                    <div class="d-flex align-center mb-8">
                        <v-avatar color="primary" size="60" class="mr-4">
                            <v-icon icon="mdi-chart-line" />
                        </v-avatar>
                        <div class="text-left">
                            <div class="text-subtitle-1 font-weight-bold">Maximize Profits</div>
                            <div class="text-body-1 text-medium-emphasis">
                                Sell unsold group inventory, fill your own groups faster, or maximize your TC benefits.
                            </div>
                        </div>
                    </div>

                    <div class="d-flex align-center mb-8">
                        <v-avatar color="primary" size="60" class="mr-4">
                            <v-icon icon="mdi-cogs" />
                        </v-avatar>
                        <div class="text-left">
                            <div class="text-subtitle-1 font-weight-bold">Agent-Friendly Tools</div>
                            <div class="text-body-1 text-medium-emphasis">
                                Access an easy-to-use dashboard to manage your listings and bookings.
                            </div>
                        </div>
                    </div>

                    <div class="d-flex align-center mb-8">
                        <v-avatar color="primary" size="60" class="mr-4">
                            <v-icon icon="mdi-bullhorn" />
                        </v-avatar>
                        <div class="text-left">
                            <div class="text-subtitle-1 font-weight-bold">Expand Your Reach</div>
                            <div class="text-body-1 text-medium-emphasis">
                                Market your space to other agents and direct-to-consumer.
                            </div>
                        </div>
                    </div>

                    <div class="d-flex align-center">
                        <v-avatar color="primary" size="60" class="mr-4">
                            <v-icon icon="mdi-headset" />
                        </v-avatar>
                        <div class="text-left">
                            <div class="text-subtitle-1 font-weight-bold">Service & Support</div>
                            <div class="text-body-1 text-medium-emphasis">
                                We deal with the client directly! No headache, just commissions.
                            </div>
                        </div>
                    </div>
                </v-col>
            </v-row>
        </v-container>

        <!-- PRICING / MEMBERSHIP -->
        <!-- PRICING / MEMBERSHIP -->
        <v-sheet class="py-12 bg-light">
            <v-container max-width="1200">
                <div class="text-center mb-8">
                    <div class="text-h3 font-weight-bold mb-3">Choose Your Membership Level</div>
                    <div class="text-body-1 text-medium-emphasis">
                        Compare features and pick the best fit for you and your business
                    </div>
                </div>

                <!-- Equal-height cards -->
                <v-row class="pricing-cards" align="stretch" justify="center" dense>
                    <v-col cols="12" md="4" v-for="p in plans" :key="p.key" class="d-flex">
                        <v-card class="pricing-card soft-bg overflow-visible d-flex flex-column w-100" rounded="xl"
                            :elevation="p.featured ? 6 : 2">
                            <div class="pill" :class="p.pillClass">{{ p.pill }}</div>

                            <v-card-text class="pa-8 d-flex flex-column flex-grow-1">
                                <!-- plan name -->
                                <div class="text-h6 text-primary text-center mb-2">{{ p.name }}</div>

                                <!-- price -->
                                <div class="text-center mb-4">
                                    <span class="display-price">
                                        <span class="price-amount">{{ p.price }}</span><span
                                            class="price-suffix">/mo</span>
                                    </span>
                                </div>

                                <!-- blurb -->
                                <p class="text-body-2 text-medium-emphasis text-center mb-6 flex-grow-0">
                                    {{ p.blurb }}
                                </p>

                                <!-- feature list -->
                                <v-list bg-color="transparent" density="comfortable" class="mb-6 flex-grow-1">
                                    <v-list-item v-for="(item, i) in p.items" :key="i" class="py-2">
                                        <template #prepend>
                                            <v-avatar size="28" class="mr-2 avatar-check">
                                                <v-icon size="18">mdi-check</v-icon>
                                            </v-avatar>
                                        </template>
                                        <v-list-item-title class="text-body-1">{{ item }}</v-list-item-title>
                                    </v-list-item>
                                </v-list>

                                <!-- CTA -->
                                <div class="mt-auto">
                                    <v-btn :variant="p.featured ? 'flat' : 'outlined'"
                                        :color="p.featured ? 'primary' : 'primary'" size="large" block
                                        :to="{ path: '/agent/signup' }">
                                        {{ p.cta }}
                                    </v-btn>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </v-sheet>

    </div>
</template>

<script setup>
const bannerImage = new URL('@/assets/imgs/service-support.png', import.meta.url).href

const plans = [
    {
        key: 'standard',
        pill: 'PERSONAL',
        pillClass: 'pill-neutral',
        name: 'Standard',
        price: '$0',
        blurb: 'Perfect to try UnBooked and list occasionally.',
        items: [
            'Marketplace Access',
            'List Your Inventory',
            '3% Listing Service Fee',
            'Standard Support'
        ],
        cta: 'Get Plan',
        featured: false
    },
    {
        key: 'enhanced',
        pill: 'POPULAR',
        pillClass: 'pill-primary',
        name: 'Enhanced',
        price: '$100',
        blurb: 'Grow faster with placement boost and analytics.',
        items: [
            'Marketplace Access',
            'List Your Inventory',
            '1.75% Listing Service Fee',
            'Placement Boost (Enhanced)',
            'Reporting Dashboard (Basic)',
            'Trusted Seller Badge',
            'Enhanced Support'
        ],
        cta: 'Get Plan',
        featured: true
    },
    {
        key: 'premium',
        pill: 'BEST VALUE',
        pillClass: 'pill-accent',
        name: 'Premium',
        price: '$175',
        blurb: 'Max visibility, marketing, and priority service.',
        items: [
            'Marketplace Access',
            'List Your Inventory',
            '1.25% Listing Service Fee',
            '1 Free Paid Promotion / mo',
            'Premium Seller Badge',
            'Featured in Newsletter / Social',
            'Marketing Toolkit',
            'Early Access to New Features',
            'Dedicated Account Manager'
        ],
        cta: 'Get Plan',
        featured: false
    }
]
</script>

<style scoped>
/* allow your existing gradient variable to drive the background */
:root,
:host {
    /* ensure var is available; fallback if not defined in global theme */
    --gradient-soft: linear-gradient(180deg, rgba(2, 0, 36, 0) 0%, rgba(0, 0, 0, 0.04) 100%);
}

/* layout */
.pricing-cards {
    --gap: 40px;
}

.pricing-cards>.v-col {
    padding-left: var(--gap);
    padding-right: var(--gap);
}

/* subtle lift on hover */
.pricing-card {
    transition: transform .25s ease, box-shadow .25s ease;
}

.pricing-card:hover {
    transform: translateY(-6px);
}

/* top pill styles */
.pill {
    position: absolute;
    top: -14px;
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 14px;
    border-radius: 999px;
    font-weight: 700;
    font-size: .75rem;
    letter-spacing: .04em;
    color: #282E98;
    background: #cfefff;
    box-shadow: 0 2px 0 rgba(0, 0, 0, .06);
}

.pill-primary {
    background: color-mix(in oklab, rgb(var(--v-theme-primary)) 30%, white);
    color: #282E98;
}

.pill-accent {
    background: color-mix(in oklab, rgb(var(--v-theme-secondary)) 30%, white);
    color: #282E98;
}

/* price typography */
.display-price {
    white-space: nowrap;
}

.price-amount {
    font-size: 2.25rem;
    font-weight: 800;
    line-height: 1;
    margin-right: 4px;
    color: #0a2a55;
}

.price-suffix {
    font-size: 1rem;
    color: rgba(0, 0, 0, .5);
}

/* check bullets */
.avatar-check {
    background: transparent;
    border: 2px solid color-mix(in oklab, rgb(var(--v-theme-primary)) 50%, white);
    color: color-mix(in oklab, rgb(var(--v-theme-primary)) 80%, black);
}

/* tighten list */
.v-list {
    padding: 0;
}

.v-list-item {
    border-radius: 10px;
}

/* spacing for small screens */
@media (max-width: 959px) {
    .pricing-cards>.v-col {
        padding-left: 12px;
        padding-right: 12px;
    }

    .price-amount {
        font-size: 2rem;
    }
}
</style>
